/**
 * Derives the Goggles Connectivity Timeline model from a session's merged
 * event list (analytics + Crashlytics, as returned by
 * /analytics/session-timeline-with-crashes). Pure — no network calls.
 *
 * See plans/goggles-connectivity-timeline/eng-plan.md for the full spec and
 * the real-data reconciliation each rule below is calibrated against.
 */

const FIVE_MINUTES_MS = 5 * 60 * 1000;

// Same thresholds as the `connect_failure` cause in
// backend/reports/goggles_connectivity/causes.js — reused, not reinvented.
const RUN_BREAK_MS = 300 * 1000;
const MIN_FAILS = 2;
const MIN_SPAN_MS = 30 * 1000;
const RECOVERY_MS = 120 * 1000;

// A GogglesDisconnected this soon after a GogglesWillPowerOff is the goggles
// switching off, whatever error the disconnect itself reports. Same window as
// RECONNECT_LOOP_POWER_OFF_SEC in backend/reports/goggles_connectivity/causes.js.
// September-October 2026: 564 of 575 power-offs were followed by a disconnect,
// median 7.4s and p99 10.6s later, and 444 of the 565 disconnects inside the
// window reported "The connection has timed out unexpectedly."
const POWER_OFF_WINDOW_MS = 15 * 1000;

// PowerOffNotice.Reason in backend/protos/goggles/diagnostics.proto
const POWER_OFF_REASONS = {
  1: 'button held',
  2: 'auto sleep',
  3: 'low battery',
  4: 'rebooting after an OTA update'
};

const CONNECTION_TIMEOUT_MESSAGE = 'The connection has timed out unexpectedly.';

const CRASH_ISSUE_CODE = '(5010)'; // HeadsUpError.gogglesCrashRecorded, confirmed via iOS agent

// GogglesOTAFlashingBegin, or OTAMetadataSent on the Android path that never sends
// Begin (GogglesOTADownloaded -> OTAMetadataSent -> GogglesOTAInstalling -> GogglesOTASuccess).
const OTA_START_EVENTS = ['GogglesOTAFlashingBegin', 'OTAMetadataSent'];
const OTA_STUCK_ISSUE_CODE = '(5011)'; // iOS Crashlytics non-fatal; Android logs GogglesOTAUploadStuck

const eventTime = (e) => (e?.timestamp ? new Date(e.timestamp).getTime() : null);
const byName = (events, name) => events.filter((e) => e.eventName === name);

const isGogglesCrashEvent = (e) =>
  e.isCrash === true && typeof e.crashDetails?.issueSubtitle === 'string' &&
  e.crashDetails.issueSubtitle.includes(CRASH_ISSUE_CODE);

const powerOffReasonText = (evt) => {
  const label = POWER_OFF_REASONS[evt.params?.reason];
  return label ? `Goggles powered off: ${label}` : 'Goggles powered off';
};

/**
 * CoreBluetooth's connection timeout (CBError 6). iOS reports it when the link
 * goes quiet, which includes the goggles going out of range or switching off,
 * so it doesn't show that anything failed.
 */
const isConnectionTimeout = (params) =>
  params?.error_reason === CONNECTION_TIMEOUT_MESSAGE ||
  (params?.error_domain === 'CBErrorDomain' && Number(params?.error_code) === 6);

/**
 * `rssi_at_time` is "2026-10-02 14:55:58 +0000". A few iOS devices on a
 * 12-hour clock send "12:32:09 PM" instead, which returns null: the reading is
 * still usable, only its age is unknown.
 */
const parseRssiTime = (value) => {
  const match = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) ([+-]\d{2})(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  return new Date(`${match[1]}T${match[2]}${match[3]}:${match[4]}`).getTime();
};

/**
 * The signal strength reported with a connection's disconnect, from the first
 * event that has one: in a Disconnected→Disconnected callback-noise pair only
 * one event carries rssi. `rssiAgeMs` is how long before the disconnect the
 * reading was taken. It's seconds on iOS, but 5% of Android readings are over
 * 8 minutes old (September-October 2026).
 */
const readDisconnectRssi = (closingEvents) => {
  const evt = closingEvents.find((e) => e.params?.rssi != null);
  if (!evt) return { rssiAtDisconnect: null, rssiAgeMs: null };
  const readAt = parseRssiTime(evt.params.rssi_at_time);
  return {
    rssiAtDisconnect: Number(evt.params.rssi),
    rssiAgeMs: readAt == null ? null : Math.max(0, eventTime(evt) - readAt)
  };
};

/**
 * A connection "ended by forget" when a GogglesForgotten in the session
 * falls between this connection's end and whichever comes first: the next
 * connection's start, or the end of the scanned window.
 */
const findEndedByForget = (connEndMs, nextConnectMs, forgottenTimes) => {
  if (connEndMs == null) return false;
  return forgottenTimes.some((t) => t >= connEndMs && t < nextConnectMs);
};

/**
 * Runs of consecutive GogglesFailedToConnect with no GogglesConnected in
 * between. A run becomes an episode only if it has >= MIN_FAILS failures,
 * spans >= MIN_SPAN_MS, and either never recovers or recovers more than
 * RECOVERY_MS after the last failure — identical semantics to
 * connect_failure, so a quick self-resolved blip doesn't count.
 */
const deriveFailureEpisodes = (sorted) => {
  const failures = sorted.filter((e) => e.eventName === 'GogglesFailedToConnect');
  const connects = sorted.filter((e) => e.eventName === 'GogglesConnected');

  // next_conn_ts: the first successful connect strictly after a given time,
  // regardless of why a run ended — matches the SQL's window-function lookahead
  // rather than only checking the event immediately following the run.
  const nextConnectAfter = (t) => {
    for (const c of connects) {
      const ct = eventTime(c);
      if (ct > t) return ct;
    }
    return null;
  };

  const episodes = [];
  let run = [];

  const closeRun = () => {
    if (run.length === 0) return;
    const first = eventTime(run[0]);
    const last = eventTime(run[run.length - 1]);
    const nextConnMs = nextConnectAfter(last);
    const recovered = nextConnMs != null && nextConnMs - last <= RECOVERY_MS;
    if (run.length >= MIN_FAILS && last - first >= MIN_SPAN_MS && !recovered) {
      episodes.push({ startTime: first, endTime: last, failureCount: run.length });
    }
    run = [];
  };

  for (const e of failures) {
    const t = eventTime(e);
    if (run.length > 0 && t - eventTime(run[run.length - 1]) > RUN_BREAK_MS) {
      closeRun();
    }
    run.push(e);
  }
  closeRun();

  return episodes;
};

const isOtaStuckEvent = (e) =>
  e.eventName === 'GogglesOTAUploadStuck' ||
  (e.isCrash === true && typeof e.crashDetails?.issueSubtitle === 'string' &&
    e.crashDetails.issueSubtitle.includes(OTA_STUCK_ISSUE_CODE));

const isOtaFailureEvent = (e) =>
  e.eventName === 'GogglesOTAFailed' || e.eventName === 'GogglesOTAPacketSendFailed' || isOtaStuckEvent(e);

const otaFirmwareVersion = (params, prefix) => {
  if (params?.[`${prefix}_majorVersion`] == null) return null;
  return ['major', 'minor', 'patch', 'buildNum']
    .map((part) => params[`${prefix}_${part}Version`] ?? 0)
    .join('.');
};

/** The firmware embeds control characters in error_reason; see docs/Analytics/Reports/ota_attempts.md. */
const otaFailureDetail = (e) => {
  if (isOtaStuckEvent(e)) {
    const otaState = e.params?.otaState ?? e.crashDetails?.customKeys?.otaState;
    return otaState ? `Upload stuck: ${otaState}` : 'Upload stuck';
  }
  const raw = e.params?.reason ?? e.params?.error_from_goggles ?? e.params?.error ?? e.params?.error_reason;
  if (raw == null) return null;
  // eslint-disable-next-line no-control-regex
  return String(raw).replace(/[\s\x00-\x1f]+/g, ' ').trim() || null;
};

const closeOtaAttempt = ({ startEvt, failureEvt, linkLostAt }, { successEvt = null, nextStartMs = null } = {}) => {
  const endEvt = successEvt ?? failureEvt;
  const startTime = eventTime(startEvt ?? endEvt);
  let outcome = 'no_outcome';
  let outcomeDetail = null;
  let endTime = linkLostAt ?? nextStartMs;

  if (successEvt) {
    outcome = 'succeeded';
    endTime = eventTime(successEvt);
  } else if (failureEvt) {
    outcome = 'failed';
    outcomeDetail = otaFailureDetail(failureEvt);
    endTime = eventTime(failureEvt);
  } else if (linkLostAt != null) {
    outcomeDetail = 'Inferred: connection lost';
  } else if (nextStartMs != null) {
    outcomeDetail = 'Inferred: retried';
  }

  const fromEither = (read) => read(startEvt?.params) ?? read(endEvt?.params) ?? null;
  const swVersion = fromEither((p) => p?.goggles_sw_version);

  return {
    startTime,
    startConfirmed: startEvt != null,
    endTime,
    durationMs: startEvt != null && endTime != null ? endTime - startTime : null,
    outcome,
    outcomeDetail,
    fromVersion: fromEither((p) => otaFirmwareVersion(p, 'currFw')) ?? (swVersion != null ? String(swVersion) : null),
    toVersion: fromEither((p) => otaFirmwareVersion(p, 'manifestFw')),
    firmwareChannel: fromEither((p) => p?.firmwareChannel),
    startEvent: startEvt,
    endEvent: endEvt
  };
};

/**
 * One attempt per OTA start event, closed by GogglesOTASuccess or the next start.
 * A success outranks an earlier failure in the same attempt: GogglesOTAUploadStuck
 * has been followed by GogglesOTASuccess 814s later with no new start. An attempt
 * with no outcome event usually lost the BLE link (the app logs nothing for that
 * and retries), so its end is the first disconnect or reconnect after the start.
 * An outcome with no start in the session becomes a point attempt.
 *
 * Exported so the OTA Attempts report (backend/reports/ota_attempts) counts
 * attempts by the same rules this panel draws them by.
 *
 * @param {Array} sorted - events in time order; needs OTA and connect/disconnect events
 */
export const deriveOtaAttempts = (sorted) => {
  const attempts = [];
  let open = null;

  for (const e of sorted) {
    if (OTA_START_EVENTS.includes(e.eventName)) {
      if (open) attempts.push(closeOtaAttempt(open, { nextStartMs: eventTime(e) }));
      open = { startEvt: e, failureEvt: null, linkLostAt: null };
    } else if (e.eventName === 'GogglesOTASuccess') {
      attempts.push(closeOtaAttempt(open ?? { startEvt: null, failureEvt: null, linkLostAt: null }, { successEvt: e }));
      open = null;
    } else if (isOtaFailureEvent(e)) {
      if (open) open.failureEvt ??= e;
      else attempts.push(closeOtaAttempt({ startEvt: null, failureEvt: e, linkLostAt: null }));
    } else if (open && open.linkLostAt == null && (e.eventName === 'GogglesDisconnected' || e.eventName === 'GogglesConnected')) {
      open.linkLostAt = eventTime(e);
    }
  }
  if (open) attempts.push(closeOtaAttempt(open));

  return attempts;
};

/**
 * @param {Array} events - merged events from /analytics/session-timeline-with-crashes
 * @returns {object|null} null when there's neither goggles activity nor a resort ping — nothing to show
 */
export function deriveGogglesConnectivityTimeline(events) {
  if (!Array.isArray(events) || events.length === 0) return null;

  const sorted = [...events].filter((e) => e.timestamp).sort((a, b) => eventTime(a) - eventTime(b));
  const lastEventMs = sorted.length > 0 ? eventTime(sorted[sorted.length - 1]) : null;

  const resortPing = byName(sorted, 'SkiingAtResort')[0] || null; // first ping wins — see product.md §7
  const resortId = resortPing?.params?.resortId ?? null;
  const resortDetectedAt = resortPing ? eventTime(resortPing) : null;

  const configRestored = byName(sorted, 'GogglesConfigRestored')[0] || null;
  const gogglesConfigRestoredAt = configRestored ? eventTime(configRestored) : null;

  const forgottenEvents = byName(sorted, 'GogglesForgotten');
  const forgottenTimes = forgottenEvents.map(eventTime);
  const gogglesForgottenAt = forgottenTimes.length > 0 ? forgottenTimes[0] : null;

  const connects = byName(sorted, 'GogglesConnected');
  const disconnects = byName(sorted, 'GogglesDisconnected');
  const resetEvents = byName(sorted, 'GogglesLastResetReason');
  // Turning the phone's Bluetooth radio off kills the goggles link without ever
  // going through the CoreBluetooth callback that fires GogglesDisconnected — so
  // it's a confirmed real disconnect cause we know is missing that specific event,
  // not the same "we have no idea" bucket as a genuinely unexplained silent drop.
  const radioOffEvents = byName(sorted, 'BluetoothRadioPower').filter(
    (e) => e.params?.power === 'false' || e.params?.power === false
  );
  const powerOffEvents = byName(sorted, 'GogglesWillPowerOff');
  const batteryEvents = byName(sorted, 'GogglesBattery').filter((e) => e.params?.battery_level != null);

  const gogglesConnectedSessions = connects.map((connectEvt, idx) => {
    const startTime = eventTime(connectEvt);
    const nextConnectMs = idx + 1 < connects.length ? eventTime(connects[idx + 1]) : Infinity;
    const disconnectEvt =
      disconnects.find((d) => {
        const dMs = eventTime(d);
        return dMs >= startTime && dMs < nextConnectMs;
      }) || null;
    // A real GogglesDisconnected doesn't always fire before the next GogglesConnected
    // (e.g. a silent BLE drop the app reconnects from directly) — only the session's
    // last connection can legitimately still be open; every earlier one ends at the
    // latest when the next connect fires, even without an explicit disconnect event.
    const impliedEnd = nextConnectMs === Infinity ? null : nextConnectMs;
    let endTime = disconnectEvt ? eventTime(disconnectEvt) : impliedEnd;
    let disconnectReason = disconnectEvt?.params?.error_reason ?? null;
    let disconnectConfirmed = disconnectEvt != null;
    const closingEvents = disconnects.filter((d) => eventTime(d) >= startTime && eventTime(d) < nextConnectMs);
    const disconnectIsTimeout = isConnectionTimeout(disconnectEvt?.params);

    // The goggles announced they were switching off. That outranks whatever the
    // disconnect reports, and ends the connection when no disconnect was logged.
    const powerOffEvt = powerOffEvents.find((e) => {
      const t = eventTime(e);
      if (t < startTime) return false;
      return disconnectEvt ? eventTime(disconnectEvt) - t >= 0 && eventTime(disconnectEvt) - t <= POWER_OFF_WINDOW_MS : t < nextConnectMs;
    }) ?? null;

    if (powerOffEvt) {
      if (!disconnectEvt) endTime = eventTime(powerOffEvt);
      disconnectReason = powerOffReasonText(powerOffEvt);
      disconnectConfirmed = true;
    } else if (!disconnectEvt) {
      const radioOff = radioOffEvents.find((e) => {
        const t = eventTime(e);
        return t > startTime && (impliedEnd == null || t <= impliedEnd);
      });
      if (radioOff) {
        endTime = eventTime(radioOff);
        disconnectReason = 'Inferred: Bluetooth Radio off';
        disconnectConfirmed = true;
      }
    }

    const windowEnd = Math.min(endTime ?? nextConnectMs, nextConnectMs, lastEventMs ?? Infinity);

    let gogglesHwModel = null;
    let gogglesSwVersion = null;
    let lastResetReason = null;
    let lastResetWasCrash = null;
    let gogglesCrashReported = false;
    let pairingPin = connectEvt.params?.goggles_pin ?? null;

    for (const e of sorted) {
      const t = eventTime(e);
      if (t < startTime || t > windowEnd) continue;
      if (e.params?.goggles_hw_model != null) gogglesHwModel = String(e.params.goggles_hw_model);
      if (e.params?.goggles_sw_version != null) gogglesSwVersion = String(e.params.goggles_sw_version);
      if (isGogglesCrashEvent(e)) gogglesCrashReported = true;
    }

    const resetEvt = resetEvents.find((r) => {
      const t = eventTime(r);
      return t >= startTime && t <= windowEnd;
    });
    if (resetEvt) {
      lastResetReason = resetEvt.params?.resetReasonInterpreted ?? null;
      const rawCrash = resetEvt.params?.isCrash;
      lastResetWasCrash = rawCrash === 1 || rawCrash === true ? true : rawCrash === 0 || rawCrash === false ? false : null;
    }

    const batteryReadings = batteryEvents
      .filter((e) => eventTime(e) >= startTime && eventTime(e) <= windowEnd)
      .map((e) => ({ level: Number(e.params.battery_level), time: eventTime(e) }));

    const endedByForget = findEndedByForget(endTime, nextConnectMs, forgottenTimes);

    return {
      startTime,
      endTime,
      durationMs: (endTime ?? lastEventMs ?? startTime) - startTime,
      gogglesHwModel,
      gogglesSwVersion,
      lastResetReason,
      lastResetWasCrash,
      gogglesCrashReported,
      disconnectReason,
      disconnectConfirmed,
      endedByPowerOff: powerOffEvt != null,
      disconnectIsTimeout,
      ...readDisconnectRssi(closingEvents),
      batteryReadings,
      pairingPin,
      endedByForget
    };
  });

  const gogglesConnectFailureEpisodes = deriveFailureEpisodes(sorted);
  const gogglesOtaAttempts = deriveOtaAttempts(sorted);

  if (
    !resortId &&
    gogglesConnectedSessions.length === 0 &&
    gogglesConnectFailureEpisodes.length === 0 &&
    gogglesOtaAttempts.length === 0
  ) {
    return null;
  }

  const gogglesPairedBeforeSkiing =
    resortDetectedAt != null &&
    gogglesConfigRestoredAt != null &&
    gogglesConfigRestoredAt <= resortDetectedAt &&
    (gogglesForgottenAt == null || gogglesForgottenAt > resortDetectedAt);

  const longConnections = gogglesConnectedSessions.filter((c) => c.durationMs > FIVE_MINUTES_MS);
  const shortConnections = gogglesConnectedSessions.filter((c) => c.durationMs <= FIVE_MINUTES_MS);

  const connectedMs = gogglesConnectedSessions.reduce((sum, c) => sum + c.durationMs, 0);

  return {
    resortId,
    resortDetectedAt,
    gogglesConfigRestoredAt,
    gogglesForgottenAt,
    gogglesPairedBeforeSkiing,
    gogglesConnectedSessions,
    longConnections,
    shortConnections,
    gogglesConnectFailureEpisodes,
    gogglesOtaAttempts,
    connectedMs,
    events: sorted
  };
}
